import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { fetchCurrentUser, patchCurrentUser } from '../../api/me';
import { getApiToken } from '../../api/authToken';
import { listAppointments, upsertAppointment } from '../../api/appointments';
import { listMeetings, upsertMeeting } from '../../api/meetings/meetings';
import * as msal from '@azure/msal-browser';
import { syncWithGoogleCalendar } from './calendarSync';
import { getMsalConfig } from './msalConfig';
import { syncWithOutlookCalendar } from './outlookSync';
import './CalendarIntegrations.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const OUTLOOK_CLIENT_ID = process.env.REACT_APP_OUTLOOK_CLIENT_ID;

function GoogleMark({ size = 24 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/** Microsoft Outlook-style mark (2019+ product colors) */
function OutlookMark({ size = 24 }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden>
      <path
        fill="#0364B8"
        d="M45 16.5c0-2.4-1.6-4.5-4-5.1L25 7v34l16-9.2c2.4-.7 4-2.7 4-5.1V16.5z"
      />
      <path fill="#0A2767" d="M23 7L7 11.4c-2.4.7-4 2.7-4 5.1v15c0 2.4 1.6 4.5 4 5.1L23 41V7z" />
      <path fill="#28A8EA" d="M25 7v17l17-8.6L33.9 8.4 25 7z" />
      <path fill="#35B8F1" d="M25 24v17l8-4.6 7.4-4.2L25 24z" />
      <path fill="#0078D4" d="M7 32.4l18 8.6V24L7 15.4v17z" />
    </svg>
  );
}

/**
 * Company-wide appointment sync (Google / Outlook). Syncs active company appointments
 * (and optionally meetings) to the connected personal calendar.
 */
const CalendarIntegrations = ({ onClose }) => {
  const { user, companyData } = useAuth();
  const { showToast } = useNotification();
  const [integrations, setIntegrations] = useState({
    google: false,
    outlook: false,
  });
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [newlyConnectedProvider, setNewlyConnectedProvider] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [includeMeetingsInSync, setIncludeMeetingsInSync] = useState(false);

  useEffect(() => {
    const fetchIntegrations = async () => {
      if (!user?.uid) return;

      try {
        const token = await getApiToken();
        const payload = await fetchCurrentUser(token);
        const data = payload?.user || {};

        setIntegrations({
          google: !!data?.calendarIntegrations?.google,
          outlook: !!data?.calendarIntegrations?.outlook,
        });
      } catch (err) {
        showToast('Failed to load integration status', 'error');
      }
    };

    fetchIntegrations();
  }, [user?.uid]);

  useEffect(() => {
    if (!showSyncDialog || isSyncing) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowSyncDialog(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSyncDialog, isSyncing]);

  useEffect(() => {
    if (showSyncDialog) {
      setIncludeMeetingsInSync(false);
    }
  }, [showSyncDialog]);

  const handleGoogleAuth = async () => {
    try {
      const initialized = await initializeGoogleApi();
      if (!initialized) {
        throw new Error('Failed to initialize Google Calendar API');
      }

      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
        callback: async (response) => {
          if (response.access_token) {
            localStorage.setItem('googleCalendarToken', response.access_token);
            localStorage.setItem(
              'googleCalendarTokenExpiry',
              new Date(Date.now() + response.expires_in * 1000).toISOString()
            );

            await patchCurrentUser({ calendarIntegrations: { google: true } });
            setIntegrations((prev) => ({ ...prev, google: true }));

            setShowSyncDialog(false);
            setTimeout(() => {
              setNewlyConnectedProvider('google');
              setShowSyncDialog(true);
            }, 300);
          }
        },
      });

      client.requestAccessToken();
    } catch (err) {
      showToast('Failed to connect to Google Calendar: ' + err.message, 'error');
    }
  };

  const handleOutlookAuth = async () => {
    try {
      if (!OUTLOOK_CLIENT_ID) {
        showToast('Outlook Calendar is not configured (missing REACT_APP_OUTLOOK_CLIENT_ID).', 'error');
        return;
      }
      const msalInstance = new msal.PublicClientApplication(getMsalConfig());
      await msalInstance.initialize();

      sessionStorage.setItem('msalRedirectUrl', window.location.href);

      const response = await msalInstance.handleRedirectPromise().catch(() => null);

      if (response) {
        if (response.account) {
          localStorage.setItem('outlookAccount', JSON.stringify(response.account));
          await patchCurrentUser({ calendarIntegrations: { outlook: true } });
          setIntegrations((prev) => ({ ...prev, outlook: true }));

          setShowSyncDialog(false);
          setTimeout(() => {
            setNewlyConnectedProvider('outlook');
            setShowSyncDialog(true);
          }, 300);
          return;
        }
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        const request = {
          scopes: ['Calendars.ReadWrite', 'User.Read'],
          prompt: 'select_account',
          loginHint: sessionStorage.getItem('preferredUsername'),
        };
        await msalInstance.loginPopup(request);

        const accountsAfter = msalInstance.getAllAccounts();
        if (accountsAfter.length > 0) {
          localStorage.setItem('outlookAccount', JSON.stringify(accountsAfter[0]));
          await patchCurrentUser({ calendarIntegrations: { outlook: true } });
          setIntegrations((prev) => ({ ...prev, outlook: true }));

          setShowSyncDialog(false);
          setTimeout(() => {
            setNewlyConnectedProvider('outlook');
            setShowSyncDialog(true);
          }, 300);
        }
      } else {
        localStorage.setItem('outlookAccount', JSON.stringify(accounts[0]));
        await patchCurrentUser({ calendarIntegrations: { outlook: true } });
        setIntegrations((prev) => ({ ...prev, outlook: true }));

        setShowSyncDialog(false);
        setTimeout(() => {
          setNewlyConnectedProvider('outlook');
          setShowSyncDialog(true);
        }, 300);
      }
    } catch (err) {
      showToast('Failed to connect to Outlook Calendar: ' + err.message, 'error');
    }
  };

  const updateIntegrationStatus = async (provider, status) => {
    if (!user?.uid) return;

    try {
      await patchCurrentUser({
        calendarIntegrations: { [provider]: status },
      });
    } catch (err) {
      showToast(`Failed to update ${provider} integration status`, 'error');
    }
  };

  const handleToggleSync = async (provider) => {
    try {
      const newStatus = !integrations[provider];
      await updateIntegrationStatus(provider, newStatus);
      setIntegrations((prev) => ({ ...prev, [provider]: newStatus }));
    } catch (err) {
      showToast(`Failed to update ${provider} sync settings`, 'error');
    }
  };

  const initializeGoogleApi = async () => {
    try {
      await new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });

      await new Promise((resolve) => {
        window.gapi.load('client', resolve);
      });

      await window.gapi.client.init({
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
      });

      const redirectUri =
        window.location.hostname === 'localhost'
          ? 'http://localhost:3006/app/overview/calendar'
          : 'https://neatschedule.com/app/overview/calendar';

      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
        redirect_uri: redirectUri,
        callback: () => {},
      });

      return tokenClient;
    } catch (error) {
      showToast('Failed to initialize Google Calendar API: ' + error.message, 'error');
      return false;
    }
  };

  const handleConfirmSync = async () => {
    try {
      if (!companyData?.id) {
        throw new Error('No company loaded');
      }

      setIsSyncing(true);
      let successCount = 0;
      const errors = [];

      let msalInstance;
      if (newlyConnectedProvider === 'google') {
        const initialized = await initializeGoogleApi();
        if (!initialized) {
          throw new Error('Failed to initialize Google Calendar API');
        }
      } else if (newlyConnectedProvider === 'outlook') {
        msalInstance = new msal.PublicClientApplication(getMsalConfig());
        await msalInstance.initialize();
      }

      const companyId = companyData.id;

      const processItems = async (items, { isMeeting }) => {
        const kindLabel = isMeeting ? 'meeting' : 'appointment';

        for (const raw of items) {
          if (!raw?.id || raw.id === 'init') continue;
          const st = (raw.status || '').toLowerCase();
          if (st === 'cancelled' || st === 'canceled' || st === 'archived') continue;
          if (raw.archived) continue;

          if (newlyConnectedProvider === 'google' && raw.googleEventId) continue;
          if (newlyConnectedProvider === 'outlook' && raw.outlookEventId) continue;

          let startDate;
          let endDate;

          try {
            if (!raw.dateTime) {
              throw new Error(`Missing dateTime for ${kindLabel} ${raw.id}`);
            }

            startDate = raw.dateTime instanceof Date ? raw.dateTime : new Date(raw.dateTime);
            endDate = raw.endTime
              ? raw.endTime instanceof Date
                ? raw.endTime
                : new Date(raw.endTime)
              : null;

            if (
              startDate &&
              (!endDate || isNaN(endDate.getTime())) &&
              typeof raw.duration === 'number'
            ) {
              endDate = new Date(startDate.getTime() + raw.duration * 60 * 1000);
            }

            if (!endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
              throw new Error(`Invalid date values for ${kindLabel} ${raw.id}`);
            }

            const clientName = isMeeting
              ? String(raw.title || raw.clientName || 'Meeting').trim() || 'Meeting'
              : raw.clientName || 'Client';

            const appointment = {
              ...raw,
              dateTime: startDate,
              endTime: endDate,
              clientName,
            };

            const persistEventId = async (field, eventId) => {
              if (isMeeting) {
                await upsertMeeting({
                  companyId,
                  id: raw.id,
                  meeting: { [field]: eventId },
                });
              } else {
                await upsertAppointment({
                  companyId,
                  id: raw.id,
                  appointment: { [field]: eventId },
                });
              }
            };

            if (newlyConnectedProvider === 'google') {
              const startTime = startDate.toISOString();
              const endTimeIso = endDate.toISOString();

              const existingEvents = await window.gapi.client.calendar.events.list({
                calendarId: 'primary',
                timeMin: startTime,
                timeMax: endTimeIso,
                singleEvents: true,
                orderBy: 'startTime',
              });

              const existingEvent = existingEvents.result.items?.some((event) => {
                const eventStart = new Date(event.start.dateTime).toISOString();
                const eventEnd = new Date(event.end.dateTime).toISOString();
                return eventStart === startTime && eventEnd === endTimeIso;
              });

              if (existingEvent) {
                continue;
              }

              const result = await syncWithGoogleCalendar(appointment, user.uid, true);

              if (result.success) {
                successCount++;
                if (result.eventId) {
                  await persistEventId('googleEventId', result.eventId);
                }
              } else {
                errors.push(`Failed to sync ${kindLabel}: ${result.error}`);
              }
            } else if (newlyConnectedProvider === 'outlook') {
              const accounts = msalInstance.getAllAccounts();
              const accessToken = await msalInstance.acquireTokenSilent({
                scopes: ['Calendars.ReadWrite', 'Calendars.Read'],
                account: accounts[0],
              });

              const startTime = startDate.toISOString();
              const endTimeIso = endDate.toISOString();

              const queryUrl = `https://graph.microsoft.com/v1.0/me/events?$filter=start/dateTime eq '${startTime}' and end/dateTime eq '${endTimeIso}'`;

              const response = await fetch(queryUrl, {
                headers: {
                  Authorization: `Bearer ${accessToken.accessToken}`,
                  'Content-Type': 'application/json',
                  Prefer: 'outlook.timezone="UTC"',
                },
              });

              if (!response.ok) {
                throw new Error(`Graph API error: ${response.status}`);
              }

              const data = await response.json();
              const existingEvent = data.value && data.value.length > 0;

              if (existingEvent) {
                continue;
              }

              const result = await syncWithOutlookCalendar(appointment, user.uid, true);

              if (result.success) {
                successCount++;
                if (result.eventId) {
                  await persistEventId('outlookEventId', result.eventId);
                }
              } else {
                errors.push(`Failed to sync ${kindLabel}: ${result.error}`);
              }
            }
          } catch (itemError) {
            errors.push(`Invalid time value for ${kindLabel} ${raw.id}: ${itemError.message}`);
          }
        }
      };

      const appointmentsResult = await listAppointments({ companyId, archived: false });
      await processItems(appointmentsResult.appointments || [], { isMeeting: false });

      if (includeMeetingsInSync) {
        const meetings = await listMeetings({ companyId, archived: false });
        await processItems(meetings, { isMeeting: true });
      }

      if (errors.length > 0) {
        showToast(`Sync completed with ${errors.length} errors: ${errors.join(', ')}`, 'warning');
      } else {
        const includedMeetings = includeMeetingsInSync;
        setShowSyncDialog(false);
        setTimeout(() => {
          const providerLabel = newlyConnectedProvider === 'google' ? 'Google' : 'Outlook';
          const eventLabel = includedMeetings ? 'events' : 'appointments';
          showToast(
            `Successfully synced ${successCount} new ${eventLabel} to ${providerLabel} Calendar`,
            'success'
          );
        }, 300);
      }
    } catch (error) {
      showToast('Failed to sync calendar: ' + error.message, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const onConfirmBackdropClick = useCallback(
    (e) => {
      if (e.target !== e.currentTarget || isSyncing) return;
      setShowSyncDialog(false);
    },
    [isSyncing]
  );

  return (
    <div className="cal-int">
      <header className="cal-int__header">
        <div>
          <h2 id="cal-int-title" className="cal-int__title">
            Calendar sync
          </h2>
          <p className="cal-int__lead">
            Connect Google or Outlook to copy appointments from your company schedule into your
            personal calendar.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="cal-int__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        ) : null}
      </header>

      <div className="cal-int__list">
        <div className="cal-int__row">
          <div className="cal-int__row-icon" aria-hidden>
            <GoogleMark size={26} />
          </div>
          <div className="cal-int__row-body">
            <h3 className="cal-int__row-title">Google Calendar</h3>
            <p
              className={`cal-int__row-status${integrations.google ? ' cal-int__row-status--ok' : ''}`}
            >
              {integrations.google ? 'Connected' : 'Not connected'}
            </p>
          </div>
          <div className="cal-int__row-actions">
            {integrations.google ? (
              <button
                type="button"
                className="cal-int__switch"
                role="switch"
                aria-checked={integrations.google}
                aria-label="Google Calendar sync enabled"
                onClick={() => handleToggleSync('google')}
              >
                <span className="cal-int__switch-knob" />
              </button>
            ) : (
              <button type="button" className="cal-int__btn-connect cal-int__btn-connect--google" onClick={handleGoogleAuth}>
                <span>Connect Google</span>
              </button>
            )}
          </div>
        </div>

        <div className="cal-int__row">
          <div className="cal-int__row-icon" aria-hidden>
            <OutlookMark size={26} />
          </div>
          <div className="cal-int__row-body">
            <h3 className="cal-int__row-title">Outlook Calendar</h3>
            <p
              className={`cal-int__row-status${integrations.outlook ? ' cal-int__row-status--ok' : ''}`}
            >
              {integrations.outlook ? 'Connected' : 'Not connected'}
            </p>
          </div>
          <div className="cal-int__row-actions">
            {integrations.outlook ? (
              <button
                type="button"
                className="cal-int__switch"
                role="switch"
                aria-checked={integrations.outlook}
                aria-label="Outlook Calendar sync enabled"
                onClick={() => handleToggleSync('outlook')}
              >
                <span className="cal-int__switch-knob" />
              </button>
            ) : (
              <button type="button" className="cal-int__btn-connect cal-int__btn-connect--outlook" onClick={handleOutlookAuth}>
                <span>Connect Outlook</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {showSyncDialog ? (
        <div
          className="cal-int__modal-backdrop"
          role="presentation"
          onClick={onConfirmBackdropClick}
          onKeyDown={(e) => e.key === 'Escape' && !isSyncing && setShowSyncDialog(false)}
        >
          <div
            className="cal-int__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cal-int-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cal-int-modal-title" className="cal-int__modal-title">
              Sync existing bookings?
            </h3>
            <p className="cal-int__modal-text">
              Copy your appointments to {newlyConnectedProvider === 'google' ? 'Google' : 'Outlook'}{' '}
              Calendar.
            </p>
            <div className="cal-int__modal-option">
              <label>
                <input
                  type="checkbox"
                  checked={includeMeetingsInSync}
                  onChange={(e) => setIncludeMeetingsInSync(e.target.checked)}
                  disabled={isSyncing}
                />
                <span>Also include company meetings from the Meetings list (optional).</span>
              </label>
            </div>
            <p className="cal-int__modal-hint">
              This may take a moment if you have many bookings.
              {isSyncing ? ' Please keep this window open.' : ''}
            </p>
            {isSyncing ? (
              <div className="cal-int__modal-progress">
                <span className="cal-int__spinner" aria-hidden />
                <span>Syncing…</span>
              </div>
            ) : null}
            <div className="cal-int__modal-actions">
              <button
                type="button"
                className="cal-int__btn cal-int__btn--ghost cal-int__btn--sm"
                onClick={() => !isSyncing && setShowSyncDialog(false)}
                disabled={isSyncing}
              >
                Skip
              </button>
              <button
                type="button"
                className="cal-int__btn cal-int__btn--primary cal-int__btn--sm"
                onClick={handleConfirmSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <>
                    <span className="cal-int__spinner" aria-hidden />
                    Syncing…
                  </>
                ) : (
                  includeMeetingsInSync ? 'Sync appointments & meetings' : 'Sync appointments'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CalendarIntegrations;
